/**
 * Mục «Sản xuất nhạc»: bài hát (`prod`) + DỰ ÁN (`folder` — mã loại là dữ liệu nên giữ nguyên tên
 * khoá, chỉ chữ trên màn hình đổi thành «dự án» ngày 23/09/2026) + QUY TRÌNH. Hàm thuần, có test.
 */
import { PROJECT_MEMBERS_KEY, type RecordDto } from '@halo/shared';
import { normalizeForComparison } from './format';
import {
  applyChanges,
  compactData,
  readOptionalString,
  readString,
  type RecordData,
} from './records/parsing';

// ------------------------------------------------------------------ quy trình

/**
 * QUY TRÌNH SẢN XUẤT NHẠC — danh sách công đoạn, SỬA ĐƯỢC ở Cài đặt › Sản xuất nhạc (chốt
 * 23/09/2026: quy trình còn đang chạy thử, đổi một bước không nên phải sửa mã nguồn).
 *
 * `id` là DỮ LIỆU: bài hát lưu mã bước trong khoá `st`, nên đổi TÊN bước không đụng tới bài nào,
 * còn xoá một bước thì bài đang đứng đó rơi về bước ĐẦU chứ không biến mất khỏi bảng. Vì vậy mã
 * được sinh một lần rồi giữ nguyên, không bao giờ đánh lại theo thứ tự.
 */
export interface ProductionStageDef {
  id: string;
  name: string;
  /** Bước này làm gì — hiện khi đưa chuột lên tên cột. */
  note: string;
}

/**
 * Sáu bước chủ dự án mô tả ngày 23/09/2026, dùng khi Giám đốc chưa sửa gì. Bước cuối là chỗ vòng
 * lặp khép lại: KD phản hồi sớm để Producer nắm tình hình; sản phẩm bị góp ý thì kéo NGƯỢC về đúng
 * bước cần làm lại — bảng quy trình đi được cả hai chiều, không chỉ tiến.
 */
/**
 * Bước «Tên bài hát - ca sỹ» KHÔNG nằm ở đây (chốt 23/09/2026): nó trùng với cột đầu của bảng, vốn
 * đã là tên bài — ca sỹ, lại còn đứng yên khi cuộn ngang. Bước ấy vẫn là một bước của quy trình,
 * chỉ là không tốn thêm một cột; ai muốn có cột riêng thì thêm lại ở Cài đặt › Sản xuất nhạc.
 */
export const DEFAULT_PRODUCTION_STAGES: readonly ProductionStageDef[] = [
  {
    id: 'demo',
    name: 'SẢN PHẨM DEMO',
    note: 'Producer và quản lý nhận folder, phối hợp ra bản demo (SUNO, hoặc bản phối bằng sound).',
  },
  {
    id: 'duyet',
    name: 'NGƯỜI DUYỆT',
    note: 'KD phản hồi TRONG NGÀY và truyền đạt ý chính xác cho quản lý và Producer.',
  },
  {
    id: 'phancong',
    name: 'NHÂN VIÊN SẢN XUẤT',
    note: 'Trưởng nhóm dự án chia việc cụ thể cho từng người sau khi đã nắm ý.',
  },
  {
    id: 'hoanthien',
    name: 'SẢN PHẨM HOÀN THIỆN',
    note: 'Producer hoàn thành sản phẩm, quản lý check lần cuối trước khi giao.',
  },
  {
    id: 'giao',
    name: 'GHI CHÚ',
    note: 'Đã giao cho KD. KD phản hồi sớm để Producer nắm tình hình mà sản xuất tiếp.',
  },
];

export const STAGE_NAME_MAX_LENGTH = 40;
export const STAGE_NOTE_MAX_LENGTH = 300;

/**
 * Đọc danh sách bước đã lưu. Mục hỏng (thiếu mã, trùng mã) bị bỏ; danh sách RỖNG → sáu bước mặc
 * định, vì một quy trình không có bước nào thì bảng không vẽ được gì và cũng chẳng ai cố ý muốn thế.
 */
export function parseProductionStages(value: unknown): ProductionStageDef[] {
  if (!Array.isArray(value)) return [...DEFAULT_PRODUCTION_STAGES];
  const seen = new Set<string>();
  const stages = value.flatMap((item): ProductionStageDef[] => {
    if (typeof item !== 'object' || item === null) return [];
    const entry = item as Record<string, unknown>;
    const id = typeof entry.id === 'string' ? entry.id.trim() : '';
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [
      {
        id,
        name: typeof entry.name === 'string' ? entry.name.trim() : id,
        note: typeof entry.note === 'string' ? entry.note.trim() : '',
      },
    ];
  });
  return stages.length ? stages : [...DEFAULT_PRODUCTION_STAGES];
}

/** Mã bước mới: «s» + số, không đụng mã nào đang dùng (kể cả mã đã bỏ rồi thêm lại). */
export function nextStageId(stages: readonly ProductionStageDef[]): string {
  const taken = new Set(stages.map((stage) => stage.id));
  for (let index = stages.length + 1; ; index += 1) {
    const id = `s${index}`;
    if (!taken.has(id)) return id;
  }
}

/** Lý do chưa đặt được tên bước, hoặc null. */
export function stageNameProblem(
  name: string,
  stages: readonly ProductionStageDef[],
  editingId?: string,
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Tên bước không được rỗng';
  if (trimmed.length > STAGE_NAME_MAX_LENGTH) {
    return `Tên bước tối đa ${STAGE_NAME_MAX_LENGTH} ký tự`;
  }
  const key = normalizeForComparison(trimmed);
  const clash = stages.some(
    (stage) => stage.id !== editingId && normalizeForComparison(stage.name) === key,
  );
  return clash ? 'Đã có bước tên này' : null;
}

/** Đổi chỗ một bước lên / xuống. Ra ngoài hai đầu thì giữ nguyên danh sách. */
export function reorderStages(
  stages: readonly ProductionStageDef[],
  id: string,
  delta: number,
): ProductionStageDef[] {
  const from = stages.findIndex((stage) => stage.id === id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= stages.length) return [...stages];
  const next = [...stages];
  const [moved] = next.splice(from, 1);
  if (moved) next.splice(to, 0, moved);
  return next;
}

/** Bước ĐẦU của quy trình — nơi bài chưa xếp, và bài mang mã bước đã bị xoá, rơi về. */
export function firstStageId(stages: readonly ProductionStageDef[]): string {
  return stages[0]?.id ?? DEFAULT_PRODUCTION_STAGES[0]?.id ?? 'demo';
}

/** Mã bước của một bài, quy về danh sách ĐANG DÙNG: mã lạ (bước đã xoá) → bước đầu. */
export function resolveStage(stage: string, stages: readonly ProductionStageDef[]): string {
  return stages.some((item) => item.id === stage) ? stage : firstStageId(stages);
}

export function stageLabel(id: string, stages: readonly ProductionStageDef[]): string {
  return stages.find((stage) => stage.id === id)?.name ?? id;
}

/**
 * MỘT lần bài đổi bước: bước vừa CHUYỂN TỚI, ai chuyển, lúc nào (ISO). «Xong lúc nào» của một bước
 * chính là thời điểm của mục KẾ TIẾP trong nhật ký — không lưu hai lần cùng một sự việc.
 */
export interface StageEntry {
  stage: string;
  by: string;
  at: string;
}

function readStageLog(value: unknown): StageEntry[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (typeof item !== 'object' || item === null) return [];
    const entry = item as Record<string, unknown>;
    return typeof entry.s === 'string' && typeof entry.at === 'string'
      ? [{ stage: entry.s, by: typeof entry.by === 'string' ? entry.by : '', at: entry.at }]
      : [];
  });
}

// ------------------------------------------------------------------ mô hình

export interface Song {
  recordId: number;
  owner: string | null;
  /** «Tên bài hát — Ca sỹ». */
  name: string;
  originalLyrics: string;
  fixedLyrics: string;
  sunoPrompt: string;
  sunoLink: string;
  /** Mã thư mục; `undefined` = «Chưa xếp». Có thể là mã CHẾT (thư mục đã xoá). */
  folderCode: string | undefined;
  /**
   * NGƯỜI SẢN XUẤT bài này (thêm 25/09/2026) — tên đăng nhập, '' = chưa gán. Khác `stageOwner`:
   * bước nào ai đang cầm thì đổi liên tục theo quy trình, còn đây là người làm ra bài, hỏi bài này
   * ai làm thì nhìn vào đây, kể cả khi bài đã xong và không còn đứng ở bước nào.
   */
  producer: string;
  /** Mã công đoạn bài đang đứng. Mã của bước ĐÃ BỊ XOÁ vẫn giữ nguyên ở đây — bảng tự quy nó về
   * bước đầu (`resolveStage`), để xoá nhầm một bước rồi thêm lại là dữ liệu về đúng chỗ. */
  stage: string;
  /** Người phụ trách công đoạn hiện tại; '' = chưa gán (coi như người nhập). */
  stageOwner: string;
  /** Nhật ký chuyển bước, cũ nhất trước. */
  stageLog: StageEntry[];
  /**
   * Ghi chú RIÊNG của từng công đoạn, khoá theo mã bước (thêm 23/09/2026). Đây là chỗ KD gõ feedback
   * ở bước «KD duyệt demo», trưởng nhóm ghi phân công ở bước «Phân công sản xuất»… — mỗi bước một ô
   * chữ, không giẫm lên nhau. Bước bị xoá khỏi quy trình thì ghi chú vẫn nằm đây, thêm lại bước là
   * chữ cũ hiện lại.
   */
  stageNotes: Record<string, string>;
  /**
   * File của bài, để trong Kho nhạc. Một danh sách phẳng, phân biệt bằng `kind` — bước nào cần loại
   * nào thì lọc ra loại ấy (thêm 23/09/2026, thay cho danh sách chỉ có bản demo).
   */
  files: SongFile[];
  /** Đường link riêng của từng bước, khoá theo mã bước (Link SUNO của bản hoàn thiện…). */
  stageLinks: Record<string, string>;
  /** Người được phân công ở từng bước, khoá theo mã bước. */
  stageAssignees: Record<string, string[]>;
}

/**
 * Loại file của một bài:
 *   demo    — bản demo (bước «Làm demo»)
 *   final   — nhạc hoàn thành, MP3 / WAV
 *   profile — hồ sơ bài hát, file nén
 *   stems   — bộ stems, file nén
 */
export const SONG_FILE_KINDS = ['demo', 'final', 'profile', 'stems'] as const;
export type SongFileKind = (typeof SONG_FILE_KINDS)[number];

export const SONG_FILE_LABELS: Record<SongFileKind, string> = {
  demo: 'Bản demo',
  final: 'Nhạc hoàn thành',
  profile: 'Profile nhạc',
  stems: 'File stems',
};

/** Một file trong Kho nhạc; `id` là mã bài trong kho, dùng để phát, để tải và để gỡ. */
export interface SongFile {
  id: string;
  name: string;
  kind: SongFileKind;
}

/**
 * DỰ ÁN (trong dữ liệu vẫn là `folder`, mã loại là dữ liệu nên giữ nguyên). Bài hát trỏ vào dự án
 * qua `code`.
 */
export interface Folder {
  recordId: number;
  /** Mã ổn định («f3» ở dữ liệu cũ, «f3@tên-đăng-nhập» ở dữ liệu mới) — bài hát trỏ tới mã này. */
  code: string;
  name: string;
  /**
   * Lúc dự án được đánh dấu XONG (ISO-8601); '' = đang chạy. Dự án xong rời bảng «Tổng hợp dự án»
   * và nằm ở «Dự án hoàn thành» — bài hát, ghi chú từng bước và file demo GIỮ NGUYÊN, không chép đi
   * đâu cả, chỉ đổi chỗ nhìn (chốt 23/09/2026).
   */
  doneAt: string;
  /** Ai đánh dấu xong. */
  doneBy: string;
  /**
   * NGƯỜI ĐƯỢC TÍCH vào dự án (tên đăng nhập). Giám đốc lập dự án rồi tích người liên quan; chỉ họ
   * xem và review dự án ấy. Danh sách RỖNG = chưa khoá, ai vào được mục Sản xuất nhạc đều thấy —
   * giữ nguyên nếp cũ cho dữ liệu đang có. Máy chủ mới là chỗ chặn thật (xem `records.service.ts`).
   */
  members: string[];
}

const readStageNotes = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const notes: Record<string, string> = {};
  for (const [stage, text] of Object.entries(value as Record<string, unknown>)) {
    if (typeof text === 'string' && text.trim()) notes[stage] = text;
  }
  return notes;
};

const isFileKind = (value: unknown): value is SongFileKind =>
  typeof value === 'string' && (SONG_FILE_KINDS as readonly string[]).includes(value);

/**
 * Đọc danh sách file. Bản ghi CŨ chỉ có khoá `demo` (toàn bản demo) — đọc thành loại `demo`, không
 * cần ai dọn dữ liệu.
 */
const readSongFiles = (data: RecordData): SongFile[] => {
  const rows = Array.isArray(data.files) ? data.files : Array.isArray(data.demo) ? data.demo : [];
  const fallbackKind: SongFileKind = Array.isArray(data.files) ? 'final' : 'demo';
  return rows.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    if (!id) return [];
    return [
      {
        id,
        name: typeof row.n === 'string' ? row.n : id,
        kind: isFileKind(row.k) ? row.k : fallbackKind,
      },
    ];
  });
};

/** Bảng «mã bước → danh sách tên đăng nhập», bỏ mục rỗng. */
const readStageAssignees = (value: unknown): Record<string, string[]> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const byStage: Record<string, string[]> = {};
  for (const [stage, list] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    const people = [...new Set(list.filter((item): item is string => typeof item === 'string'))];
    if (people.length) byStage[stage] = people;
  }
  return byStage;
};

/**
 * Ai đứng tên NGƯỜI SẢN XUẤT / nhận phân công sản xuất: nhân sự của ĐƠN VỊ LÀM NHẠC mà Giám đốc
 * tích ở Cài đặt. Chưa tích đơn vị nào thì mở cho mọi người — khoá cứng lại thì ô chọn trống trơn
 * mà chẳng ai biết vì sao.
 */
export function producerCandidates<T extends { departmentId: string; teamId: string }>(
  users: readonly T[],
  producerUnits: readonly string[],
): T[] {
  const units = new Set(producerUnits);
  if (!units.size) return [...users];
  return users.filter((person) => units.has(person.departmentId) || units.has(person.teamId));
}

export function parseSong(record: RecordDto): Song {
  const data = record.data;
  return {
    recordId: record.id,
    owner: record.owner,
    name: readString(data, 'name'),
    originalLyrics: readString(data, 'lyricRaw'),
    fixedLyrics: readString(data, 'lyricFix'),
    sunoPrompt: readString(data, 'prompt'),
    sunoLink: readString(data, 'link'),
    folderCode: readOptionalString(data, 'folder'),
    producer: readString(data, 'prodby'),
    // Bài có từ TRƯỚC khi có quy trình chưa mang khoá `st` — đoán bước theo dữ liệu đã điền, để 24
    // bài đang có rơi sẵn vào đúng cột thay vì dồn hết về bước đầu (xem `guessStage`).
    stage: data.st === undefined ? guessStage(data) : readString(data, 'st'),
    stageOwner: readString(data, 'sby'),
    stageLog: readStageLog(data.log),
    stageNotes: readStageNotes(data.note),
    stageLinks: readStageNotes(data.slink),
    stageAssignees: readStageAssignees(data.sasg),
    files: readSongFiles(data),
  };
}

/**
 * Đoán bước của bài CŨ theo những gì đã điền: có link SUNO là đã ra sản phẩm, còn lại là đang ở
 * bước đầu. Chỉ dùng khi bản ghi chưa có khoá `st`; chuyển bước một lần là bài mang mã thật và
 * không đoán nữa.
 *
 */
function guessStage(data: RecordData): string {
  if (readString(data, 'link').trim()) return 'hoanthien';
  if (readString(data, 'prompt').trim() || readString(data, 'lyricFix').trim()) return 'demo';
  return DEFAULT_PRODUCTION_STAGES[0]?.id ?? 'demo';
}

function toSongData(song: Song): RecordData {
  return {
    name: song.name,
    lyricRaw: song.originalLyrics,
    lyricFix: song.fixedLyrics,
    prompt: song.sunoPrompt,
    link: song.sunoLink,
    folder: song.folderCode ?? null,
    prodby: song.producer || null,
    st: song.stage,
    sby: song.stageOwner || null,
    log: song.stageLog.length
      ? song.stageLog.map((entry) => ({ s: entry.stage, by: entry.by, at: entry.at }))
      : null,
    note: Object.keys(song.stageNotes).length ? song.stageNotes : null,
    slink: Object.keys(song.stageLinks).length ? song.stageLinks : null,
    sasg: Object.keys(song.stageAssignees).length ? song.stageAssignees : null,
    files: song.files.length
      ? song.files.map((file) => ({ id: file.id, n: file.name, k: file.kind }))
      : null,
    // Khoá cũ chỉ chứa bản demo: đã đọc sang `files` rồi thì bỏ, khỏi giữ hai chỗ lệch nhau.
    demo: null,
  };
}

/** Đặt ghi chú của MỘT bước; chuỗi rỗng là xoá ghi chú bước đó chứ không để lại ô trắng. */
export function withStageNote(song: Song, stage: string, text: string): Song {
  const stageNotes = { ...song.stageNotes };
  if (text.trim()) stageNotes[stage] = text;
  else delete stageNotes[stage];
  return { ...song, stageNotes };
}

/** Đặt link của MỘT bước; chuỗi rỗng là xoá. */
export function withStageLink(song: Song, stage: string, link: string): Song {
  const stageLinks = { ...song.stageLinks };
  if (link.trim()) stageLinks[stage] = link.trim();
  else delete stageLinks[stage];
  return { ...song, stageLinks };
}

/** Đặt danh sách người phân công của MỘT bước; danh sách rỗng là bỏ phân công bước đó. */
export function withStageAssignees(song: Song, stage: string, people: readonly string[]): Song {
  const stageAssignees = { ...song.stageAssignees };
  const cleaned = [...new Set(people.filter(Boolean))];
  if (cleaned.length) stageAssignees[stage] = cleaned;
  else delete stageAssignees[stage];
  return { ...song, stageAssignees };
}

/** Thêm / bớt file. Trùng mã thì không thêm lần hai. */
export function withSongFile(song: Song, file: SongFile): Song {
  if (song.files.some((item) => item.id === file.id)) return song;
  return { ...song, files: [...song.files, file] };
}

export function withoutSongFile(song: Song, fileId: string): Song {
  return { ...song, files: song.files.filter((file) => file.id !== fileId) };
}

/** Đổi tên hiển thị của một file đã gắn — theo tên máy chủ trả về sau khi đổi (đuôi giữ nguyên). */
export function withRenamedSongFile(song: Song, fileId: string, name: string): Song {
  if (!song.files.some((file) => file.id === fileId)) return song;
  return {
    ...song,
    files: song.files.map((file) => (file.id === fileId ? { ...file, name } : file)),
  };
}

/**
 * THAY một file bằng file vừa đưa lên: đứng đúng chỗ và đúng loại của file cũ, để bản «nhạc hoàn
 * thành» thay xong không nhảy xuống cuối hay đổi khung. Không thấy file cũ thì gắn thêm như thường.
 */
export function withReplacedSongFile(song: Song, oldId: string, file: SongFile): Song {
  if (!song.files.some((item) => item.id === oldId)) return withSongFile(song, file);
  return {
    ...song,
    files: song.files.map((item) => (item.id === oldId ? { ...file, kind: item.kind } : item)),
  };
}

/** Tên thư mục Kho nhạc mà bảng quy trình tự lập để chứa file của người làm nhạc. */
export const PRODUCTION_FOLDER_NAME = 'Sản xuất nhạc';

/**
 * Thư mục Kho nhạc để bảng quy trình cất file (28/09/2026).
 *
 * Ưu tiên thư mục tên «Sản xuất nhạc» — thư mục bảng này tự lập — để file của quy trình không rơi
 * vào thư mục đầu tiên tình cờ đứng trước theo bảng chữ cái (trước đây một bản WAV hoàn thiện đã
 * nằm trong «Acoustic tháng 7» chỉ vì thế). Không có thì mới lấy thư mục đầu tiên, như cũ.
 */
export function productionFolderId(
  folders: readonly { id: string; name: string }[],
): string | null {
  const wanted = PRODUCTION_FOLDER_NAME.trim().toLowerCase();
  const own = folders.find((folder) => folder.name.trim().toLowerCase() === wanted);
  return (own ?? folders[0])?.id ?? null;
}

/** File của bài theo từng loại, giữ đúng thứ tự đưa lên. */
export function filesOfKind(song: Song, kind: SongFileKind): SongFile[] {
  return song.files.filter((file) => file.kind === kind);
}

export function serializeSong(song: Song, stored?: RecordDto): RecordData {
  const next = toSongData(song);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toSongData(parseSong(stored)), next));
}

export function parseFolder(record: RecordDto): Folder {
  return {
    recordId: record.id,
    code: readString(record.data, 'id') || `rec:${record.id}`,
    name: readString(record.data, 'name'),
    doneAt: readString(record.data, 'done'),
    doneBy: readString(record.data, 'doneBy'),
    members: readMembers(record.data[PROJECT_MEMBERS_KEY]),
  };
}

const readMembers = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.filter((item): item is string => typeof item === 'string' && !!item))];
};

const folderData = (folder: Folder) => ({
  id: folder.code,
  name: folder.name,
  done: folder.doneAt || null,
  doneBy: folder.doneBy || null,
  [PROJECT_MEMBERS_KEY]: folder.members.length ? folder.members : null,
});

export function serializeFolder(folder: Folder, stored?: RecordDto): RecordData {
  const next = folderData(folder);
  // Tạo MỚI: `null` là để `applyChanges` XOÁ một khoá đang có, bản ghi mới thì không có gì mà xoá —
  // ghi `null` xuống chỉ tổ để lại hai khoá rỗng trong mọi dự án.
  if (!stored) {
    return compactData({
      ...next,
      done: folder.doneAt || undefined,
      doneBy: folder.doneBy || undefined,
      [PROJECT_MEMBERS_KEY]: folder.members.length ? folder.members : undefined,
    });
  }
  return compactData(applyChanges(stored.data, folderData(parseFolder(stored)), next));
}

/** Dự án mới toanh, chưa xong. */
export function newFolder(code: string, name: string): Folder {
  return { recordId: 0, code, name, doneAt: '', doneBy: '', members: [] };
}

/** Đặt lại danh sách người được tích; bỏ trùng và bỏ tên rỗng. */
export function withFolderMembers(folder: Folder, members: readonly string[]): Folder {
  return { ...folder, members: [...new Set(members.filter(Boolean))] };
}

/** Ai được xem dự án này: chưa tích ai thì mở, tích rồi thì đúng người trong danh sách. */
export function canSeeProject(folder: Folder, username: string): boolean {
  return folder.members.length === 0 || folder.members.includes(username);
}

export const isFolderDone = (folder: Folder) => Boolean(folder.doneAt);

/** Đánh dấu xong / mở lại. Mở lại thì xoá sạch dấu vết xong, không để lại ngày cũ gây hiểu nhầm. */
export function withFolderDone(folder: Folder, done: boolean, by: string, now: Date): Folder {
  return done
    ? { ...folder, doneAt: now.toISOString(), doneBy: by }
    : { ...folder, doneAt: '', doneBy: '' };
}

/** Mã của các dự án ĐÃ XONG — bài thuộc các mã này không hiện ở bảng đang chạy. */
export function doneFolderCodes(folders: readonly Folder[]): Set<string> {
  return new Set(folders.filter(isFolderDone).map((folder) => folder.code));
}

// ------------------------------------------------------------------ gom theo thư mục & tìm kiếm

export const UNFILED = '_none';

export interface FolderGroup {
  /** `null` = nhóm giả «Chưa xếp». */
  folder: Folder | null;
  /** Bài khớp ô tìm (hoặc mọi bài khi không tìm). */
  songs: Song[];
  /** Tổng số bài của thư mục trong phần đang xem, chưa tính ô tìm. */
  totalSongs: number;
}

/**
 * Mã thư mục kiểu cũ («f9») do TRÌNH DUYỆT tự đếm, nên hai người bấm «Thêm thư mục» cùng lúc sinh
 * ra HAI thư mục cùng mã — đã xảy ra thật (dữ liệu 18/08/2026). Bản cũ vì thế hiện cùng một bài ở
 * cả hai thư mục và đếm nó hai lần. Ở đây mỗi bài chỉ thuộc MỘT thư mục: thư mục tạo SAU CÙNG
 * trong số các thư mục trùng mã (đó là cái người dùng còn làm việc tiếp — đặt lại tên, thêm bài).
 */
export function folderHoldingCode(folders: readonly Folder[]): Map<string, Folder> {
  const holders = new Map<string, Folder>();
  for (const folder of folders) {
    const current = holders.get(folder.code);
    if (!current || folder.recordId > current.recordId) holders.set(folder.code, folder);
  }
  return holders;
}

/**
 * Danh sách cho ô «xếp vào thư mục nào»: mỗi MÃ đúng một dòng — thư mục đang giữ mã đó. Liệt kê cả
 * hai thư mục trùng mã là hai lựa chọn CÙNG giá trị: chọn cái nào bài cũng rơi vào cùng một chỗ.
 */
export function folderChoices(folders: readonly Folder[]): Folder[] {
  const holders = folderHoldingCode(folders);
  return folders.filter((folder) => holders.get(folder.code) === folder);
}

/** Các nhóm thư mục đang dùng CHUNG một mã — để báo cho người dùng dọn. */
export function findDuplicateFolderCodes(folders: readonly Folder[]): Folder[][] {
  const byCode = new Map<string, Folder[]>();
  for (const folder of folders)
    byCode.set(folder.code, [...(byCode.get(folder.code) ?? []), folder]);
  return [...byCode.values()].filter((group) => group.length > 1);
}

// ------------------------------------------------------------------ bảng quy trình

export interface StageColumn extends ProductionStageDef {
  songs: Song[];
}

/**
 * Mỗi công đoạn một cột, ĐỦ mọi cột kể cả cột rỗng — cột trống cũng là một thông tin. Bài mang mã
 * bước không còn trong danh sách (bước vừa bị xoá) rơi về cột ĐẦU, không biến mất khỏi bảng.
 */
export function buildStageBoard(
  songs: readonly Song[],
  stages: readonly ProductionStageDef[],
): StageColumn[] {
  return stages.map((stage) => ({
    ...stage,
    // Bài vào bước SAU CÙNG nằm trên đầu: cột nào cũng đọc từ trên xuống là từ mới tới cũ.
    songs: songs
      .filter((song) => resolveStage(song.stage, stages) === stage.id)
      .sort((a, b) => enteredAt(b).localeCompare(enteredAt(a)) || b.recordId - a.recordId),
  }));
}

/** Số bài đang đứng ở mỗi bước — cho màn Cài đặt cảnh báo trước khi xoá một bước. */
export function countSongsByStage(
  songs: readonly Song[],
  stages: readonly ProductionStageDef[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const song of songs) {
    const id = resolveStage(song.stage, stages);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

/** Thời điểm bài VÀO bước hiện tại (ISO); '' khi chưa từng chuyển bước. */
export function enteredAt(song: Song): string {
  const last = song.stageLog.at(-1);
  return last && last.stage === song.stage ? last.at : '';
}

/**
 * Đã nằm ở bước này bao nhiêu NGÀY (làm tròn xuống). `null` khi chưa từng chuyển bước — không biết
 * thì nói không biết, đừng vẽ ra số 0 ngày.
 */
export function daysInStage(song: Song, now: Date): number | null {
  const at = enteredAt(song);
  if (!at) return null;
  const started = new Date(at).getTime();
  if (Number.isNaN(started)) return null;
  return Math.max(0, Math.floor((now.getTime() - started) / 86_400_000));
}

/**
 * Bài chuyển sang bước khác: ghi thêm MỘT mục nhật ký (ai chuyển, lúc nào) và đặt người phụ trách
 * bước mới. Trả về bài đã đổi; bước không đổi thì trả về chính nó, người gọi khỏi ghi vô ích.
 */
export function moveToStage(
  song: Song,
  stage: string,
  by: string,
  now: Date,
  stageOwner = by,
): Song {
  if (song.stage === stage) return song;
  return {
    ...song,
    stage,
    stageOwner,
    stageLog: [...song.stageLog, { stage, by, at: now.toISOString() }],
  };
}

// ------------------------------------------------------------------ xuất danh sách

/** Đầu bảng của file danh sách bài — ba cột, đúng mẫu chủ dự án yêu cầu (23/09/2026). */
export const SONG_LIST_HEADER = ['STT', 'Tên bài hát — Ca sỹ', 'Link đính kèm'] as const;

/**
 * Danh sách bài → bảng ba cột để xuất ra file: số thứ tự · tên bài hát — ca sỹ · link đính kèm
 * (link SUNO của bài). STT đánh theo THỨ TỰ ĐANG HIỆN trên màn hình, không phải theo mã bản ghi —
 * lọc rồi xuất thì số phải chạy 1, 2, 3 trên đúng những dòng người ta đang thấy.
 */
export function songListRows(songs: readonly Song[]): string[][] {
  return songs.map((song, index) => [
    String(index + 1),
    song.name || '(chưa đặt tên)',
    song.sunoLink,
  ]);
}

const nonBlankLines = (text: string) => text.split(/\r?\n/).filter((line) => line.trim());
export const countLines = (text: string) => nonBlankLines(text).length;
export const firstLine = (text: string) => nonBlankLines(text)[0]?.trim() ?? '';

export function matchesSearch(song: Song, folderName: string, query: string): boolean {
  const wanted = normalizeForComparison(query);
  if (!wanted) return true;
  const haystack = [song.name, song.originalLyrics, song.fixedLyrics, song.sunoPrompt, folderName];
  return normalizeForComparison(haystack.join(' ')).includes(wanted);
}

/**
 * Thư mục theo thứ tự khai, rồi tới «Chưa xếp». Bài trỏ tới mã thư mục CHẾT cũng rơi vào «Chưa
 * xếp». «Chưa xếp» không có bài thì ẩn; đang tìm thì thư mục không khớp bài nào cũng ẩn.
 */
export function groupSongs(
  folders: readonly Folder[],
  songs: readonly Song[],
  query: string,
): FolderGroup[] {
  const holders = folderHoldingCode(folders);
  const searching = normalizeForComparison(query) !== '';
  const build = (folder: Folder | null): FolderGroup => {
    const members = songs.filter((song) => {
      const holder = song.folderCode ? holders.get(song.folderCode) : undefined;
      return folder ? holder?.recordId === folder.recordId : holder === undefined;
    });
    const name = folder?.name ?? 'Chưa xếp';
    return {
      folder,
      songs: members.filter((song) => matchesSearch(song, name, query)),
      totalSongs: members.length,
    };
  };
  const groups = [...folders.map(build), build(null)];
  return groups.filter((group) => {
    if (searching) return group.songs.length > 0;
    return group.folder !== null || group.totalSongs > 0;
  });
}

/** Tên mặc định chưa ai dùng: «Thư mục mới», «Thư mục mới 2»… */
export function nextFolderName(folders: readonly Folder[]): string {
  const taken = new Set(folders.map((folder) => normalizeForComparison(folder.name)));
  for (let index = 1; ; index += 1) {
    const name = index === 1 ? 'Dự án mới' : `Dự án mới ${index}`;
    if (!taken.has(normalizeForComparison(name))) return name;
  }
}

export function folderNameProblem(name: string, folders: readonly Folder[], editing?: Folder) {
  const wanted = normalizeForComparison(name);
  if (!wanted) return 'Tên dự án không được rỗng';
  const clash = folders.some(
    (folder) =>
      folder.recordId !== editing?.recordId && normalizeForComparison(folder.name) === wanted,
  );
  return clash ? 'Đã có dự án tên này' : null;
}

// ------------------------------------------------------------------ kiểm khi lưu bài

export const isSunoLink = (link: string) => /^https?:\/\/(www\.)?suno\.(com|ai)\//i.test(link);
/**
 * Độ dài tối đa của ô Prompt SUNO. Trước 25/09/2026 đây là mốc CẢNH BÁO 200 ký tự (ô Style của
 * SUNO đời cũ), nhưng chủ dự án viết prompt dài hơn nhiều và bản SUNO đang dùng nhận được — nay
 * thành giới hạn thật 2.000 ký tự, không còn vạch vàng giữa chừng.
 */
export const SUNO_PROMPT_LIMIT = 2000;

/**
 * Trùng tên xét trên MỌI bài ĐÃ NẠP VỀ. Từ 24/09/2026 danh sách không còn lọc theo người nhập nữa
 * (bài là việc chung), nên bài nào có trong danh sách là bài mình thấy — không còn cảnh báo «có
 * người đã nhập bài trùng tên mà bạn không thấy».
 */
export function songProblem(
  draft: Pick<Song, 'name' | 'sunoLink'>,
  allSongs: readonly Song[],
  editing?: Song,
): string | null {
  if (!draft.name.trim()) return 'Chưa nhập tên bài hát — ca sỹ';
  if (draft.sunoLink.trim() && !/^https?:\/\//i.test(draft.sunoLink.trim())) {
    return 'Link phải bắt đầu bằng https://';
  }
  const wanted = normalizeForComparison(draft.name);
  const clash = allSongs.find(
    (song) => song.recordId !== editing?.recordId && normalizeForComparison(song.name) === wanted,
  );
  return clash ? 'Đã có bài trùng tên trong danh sách' : null;
}
